/**
 * Sidebar navigation tree as pure data.
 *
 * Behaviour (onSelect handlers, lazy children, permission filtering) is attached
 * by `useNavTree`, so this module stays trivially testable and free of hooks.
 *
 * @module NavItems
 */

import { FlaskConical, Store } from "lucide-react"
import type { NavSection } from "@strivelabs/ui-blocks/v2"
import {
  DatabaseIcon,
  GlossaryIcon,
  HomeIcon,
  ReportsIcon,
  RoutinesIcon,
  SkillsIcon,
  TasksIcon,
  WorkforceIcon,
} from "../icons/SidebarIcons"
import { reportsMatcher, startsWith } from "./matchNav"

/** Ids of items only StriveLabs internal users see. */
export const INTERNAL_NAV_IDS = ["marketplace", "experiments"] as const

/**
 * Sidebar tree for a signed-in, non-guest user, grouped as the Home Shell
 * design groups it: the two everyday destinations unlabelled at the top, then
 * WORKBENCH (where work is produced) and INTELLIGENCE (what the workspace
 * knows and who does the knowing). The design also lists Blogs under
 * Workbench; that entry waits on a blogs page and is deliberately absent.
 *
 * Icons are the sidebar's own traced glyph set (`../icons/SidebarIcons`) so
 * the rail matches the design stroke for stroke; they draw in `currentColor`
 * at the row's font size, so the tree owns their colour and scale.
 */
export const NAV_ITEMS: NavSection[] = [
  {
    id: "main",
    items: [
      {
        id: "home",
        label: "Home",
        href: "/ui",
        icon: <HomeIcon />,
        match: pathname => pathname === "/ui",
      },
      {
        id: "tasks",
        label: "Tasks",
        href: "/ui/tasks",
        icon: <TasksIcon />,
        match: startsWith("/ui/tasks"),
      },
    ],
  },
  {
    id: "workbench",
    label: "Workbench",
    items: [
      {
        id: "reports",
        label: "Reports",
        href: "/ui/reports",
        icon: <ReportsIcon />,
        match: reportsMatcher,
      },
    ],
  },
  {
    id: "intelligence",
    label: "Intelligence",
    items: [
      {
        id: "knowledge-hub",
        label: "Marketing database",
        href: "/ui/knowledge-hub",
        icon: <DatabaseIcon />,
        match: startsWith("/ui/knowledge-hub"),
      },
      {
        id: "glossary",
        label: "Glossary",
        href: "/ui/glossary",
        icon: <GlossaryIcon />,
        match: startsWith("/ui/glossary"),
      },
      {
        id: "routines",
        label: "Routines",
        href: "/ui/routines",
        icon: <RoutinesIcon />,
        match: startsWith("/ui/routines"),
      },
      {
        id: "skills",
        label: "Skills",
        href: "/ui/skills",
        icon: <SkillsIcon />,
        match: startsWith("/ui/skills"),
      },
      {
        id: "workforce",
        label: "My Workforce",
        href: "/ui/my-workforce",
        icon: <WorkforceIcon />,
        match: startsWith("/ui/my-workforce"),
      },
    ],
  },
  {
    id: "internal",
    label: "Internal",
    items: [
      {
        id: "marketplace",
        label: "Marketplace",
        href: "/ui/marketplace",
        icon: <Store size={15} />,
      },
      {
        id: "experiments",
        label: "Experiments",
        href: "/ui/experiments",
        icon: <FlaskConical size={15} />,
      },
    ],
  },
]

/**
 * Nav shown to guests — a single Reports entry. It stays active while viewing a
 * report-surfaced agent (for example AEO), so guests reach those reports from
 * within the Reports section rather than a separate nav item.
 */
export const GUEST_NAV_ITEMS: NavSection[] = [
  {
    id: "top",
    items: [
      {
        id: "reports",
        label: "Reports",
        href: "/ui/reports",
        icon: <ReportsIcon />,
        match: reportsMatcher,
      },
    ],
  },
]
